import React, { useEffect, useRef, useState } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import PropTypes from "prop-types";
import {
  InputContainer,
  ClearWrapper,
  ClearIcon,
  InputWrapper,
  StyledInput,
} from "./styles";
import { GOOGLE_MAPS_CONFIG } from "../../../../config/googleMapsConfig";

import "./google-places-override.css";

export const getApiLoader = (autoCompleteRef, onSearchStateChange) => {
  const inputElement = autoCompleteRef.current;
  if (!inputElement || !window.google?.maps?.places?.Autocomplete) {
    return;
  }

  const placesAutoComplete = new window.google.maps.places.Autocomplete(
    inputElement,
    {
      types: ["locality", "postal_code"],
      componentRestrictions: { country: "us" },
    }
  );

  placesAutoComplete.setFields(["geometry", "name"]);
  placesAutoComplete.addListener("place_changed", () =>
    handleSelection(onSearchStateChange, placesAutoComplete)
  );

  return placesAutoComplete;
};

export const handleSelection = (onSearchStateChange, placesAutoComplete) => {
  const place = placesAutoComplete.getPlace();

  if (place) {
    const { geometry, name } = place;
    if (geometry) {
      const geo = place.geometry.location.toJSON();
      const { lat, lng } = geo;
      onSearchStateChange({
        lat,
        lng,
        suggestionValue: name,
      });
    }
  }
};

const PlacesLocationInput = ({
  placeholder,
  id,
  onChange,
  defaultValue,
  onClear,
}) => {
  const [locationValue, setLocationValue] = useState(defaultValue || "");
  const autoCompleteRef = useRef(null);

  // Use the same loader as the map to avoid duplicate script loading
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_CONFIG);

  const handleClear = () => {
    const locationInput = document.getElementById(id);
    if (locationInput) locationInput.value = "";
    setLocationValue("");
    if (onClear) {
      onClear();
    }
  };

  useEffect(() => {
    if (!isLoaded || !autoCompleteRef.current) {
      return;
    }

    const placesAutoComplete = getApiLoader(autoCompleteRef, onChange);

    return () => {
      if (placesAutoComplete && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(placesAutoComplete);
      }
    };
  }, [isLoaded, onChange]);

  return (
    <InputContainer>
      <InputWrapper>
        <StyledInput
          type="text"
          className="form-control"
          placeholder={placeholder}
          id={id}
          onChange={(event) => setLocationValue(event.target.value)}
          ref={autoCompleteRef}
          defaultValue={defaultValue}
        />
        <ClearWrapper onClick={handleClear}>
          {locationValue && <ClearIcon />}
        </ClearWrapper>
      </InputWrapper>
    </InputContainer>
  );
};

PlacesLocationInput.propTypes = {
  placeholder: PropTypes.string,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
};

const elementId = "location-zipcode";

export const defaultPlacesConfig = {
  placeholder: "City or Zip Code",
  id: elementId,
};

export default PlacesLocationInput;
